import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ChevronRight, Globe, Users, BookOpen, MapPin, Calendar, Clock } from 'lucide-react';

const Experiences = () => {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [isOceanBloomModalOpen, setIsOceanBloomModalOpen] = useState(false);
  const [isWorkshopsModalOpen, setIsWorkshopsModalOpen] = useState(false);
  const [isPassportModalOpen, setIsPassportModalOpen] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.1 }
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleOceanBloomClick = () => {
    setIsOceanBloomModalOpen(true);
  };

  const handleWorkshopsClick = () => {
    setIsWorkshopsModalOpen(true);
  };

  const handlePassportClick = () => {
    setIsPassportModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-stone-50">

      {/* Workshops & Gatherings Header Section */}
      <section
        id="workshops"
        ref={el => sectionRefs.current.workshops = el}
        className={`py-12 bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('workshops')
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-4">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Workshops & Gatherings</h2>
            </div>
            <p className="text-gray-600 leading-relaxed text-lg max-w-2xl mx-auto">
              Momentum is built through consistency. Our workshops and community activities keep progress active, reinforcing skills and connections long after a single session.
            </p>
          </div>
        </div>
      </section>

      {/* Ocean Bloom Section */}
      <section
        id="ocean-bloom"
        ref={el => sectionRefs.current['ocean-bloom'] = el}
        className={`py-20 bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('ocean-bloom')
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="w-full h-96 bg-gray-100 rounded-2xl overflow-hidden">
                <video
                  src="/ocean_bloom.MP4"
                  title="Ocean Bloom Experience"
                  className="w-full h-full object-cover"
                  controls
                  preload="metadata"
                />
              </div>
            </div>
            <div className="space-y-4 text-center">
              <div>
                <h3 className="text-xs text-gray-500 mb-1">OCEAN EXPERIENCE</h3>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Ocean Bloom</h4>
              </div>
              <p className="text-gray-600 leading-relaxed text-sm max-w-xs mx-auto">
                The most profound growth happens when learning moves beyond theory. Ocean Bloom invites deep reflection and connection through guided dialogue at sea, combining natural surroundings with meaningful conversations that inspire clarity and renewal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* International Passport Society Section */}
      <section
        id="passport-society"
        ref={el => sectionRefs.current['passport-society'] = el}
        className={`py-20 bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('passport-society')
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="lg:order-2">
              <div className="w-full h-96 bg-gray-100 rounded-2xl overflow-hidden">
                <img
                  src="/international.jpg"
                  alt="International Passport Society"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:order-1 space-y-4 text-center">
              <div>
                <h3 className="text-xs text-gray-500 mb-1">GLOBAL EXPERIENCE</h3>
                <h4 className="text-xl font-bold text-gray-900 mb-3">The International Passport Society</h4>
              </div>
              <p className="text-gray-600 leading-relaxed text-sm max-w-xs mx-auto">
                Transformation broadens when you explore beyond your comfort zone. The Passport Society offers global travel, cultural exchange, and curated experiences that foster adaptability, cultural understanding, and personal growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="cta"
        ref={el => sectionRefs.current.cta = el}
        className={`bg-stone-50 transition-all duration-1500 delay-200 ${
          visibleSections.has('cta') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="w-full h-px bg-black"></div>
        <div className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Ready to Begin Your <span className="relative inline-block">
                <span className="relative z-10">Transformation</span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-200 to-purple-200 opacity-30 rounded-lg transform -rotate-1"></span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-100 to-purple-100 opacity-50 rounded-lg transform rotate-1"></span>
              </span>?
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Join our global community of empowered women and professionals. Your journey to healing, growth, and connection starts today.
            </p>
            <button 
              onClick={() => {
                navigate('/contact');
                window.scrollTo(0, 0);
              }}
              className="px-4 py-2 border rounded-full text-sm transition-colors duration-200" 
              style={{ 
                borderColor: '#6a6acb', 
                color: '#6a6acb',
                backgroundColor: 'transparent'
              }} 
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#6a6acb';
                e.target.style.color = 'white';
              }} 
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6a6acb';
              }}
            >
              Get Started Today →
            </button>
          </div>
        </div>
        <div className="w-full h-px bg-black"></div>
      </section>

      {/* Ocean Bloom Modal */}
      <Dialog open={isOceanBloomModalOpen} onOpenChange={setIsOceanBloomModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Ocean Bloom Experience
            </DialogTitle>
          </DialogHeader>
          <div className="mt-6 space-y-6">
            <div className="aspect-video bg-gray-100 rounded-2xl overflow-hidden">
              <iframe
                src="https://www.dropbox.com/scl/fi/7m5u772mcerdvhch8y0w4/OCEAN-BLOOM_ENGLISH.mp4?rlkey=ut1b4vnktz8as99951ad0qh1i&st=e70y9vjl&dl=0"
                title="Ocean Bloom Experience"
                className="w-full h-full"
                allowFullScreen
              />
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-900">What to Expect</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <ChevronRight className="w-5 h-5 mr-2 text-purple-600 mt-0.5" />
                  <span>Guided dialogue sessions in natural marine settings</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-5 h-5 mr-2 text-purple-600 mt-0.5" />
                  <span>Small group experiences for deep connection</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-5 h-5 mr-2 text-purple-600 mt-0.5" />
                  <span>Structured reflection and personal growth activities</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-5 h-5 mr-2 text-purple-600 mt-0.5" />
                  <span>Combination of natural therapy and meaningful conversation</span>
                </li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Workshops Modal */}
      <Dialog open={isWorkshopsModalOpen} onOpenChange={setIsWorkshopsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Workshops & Gatherings
            </DialogTitle>
          </DialogHeader>
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900">Skill Building Workshops</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <ChevronRight className="w-5 h-5 mr-2 text-blue-600 mt-0.5" />
                    <span>Practical skill development sessions</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="w-5 h-5 mr-2 text-blue-600 mt-0.5" />
                    <span>Hands-on learning experiences</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="w-5 h-5 mr-2 text-blue-600 mt-0.5" />
                    <span>Expert-led training programs</span>
                  </li>
                </ul>
              </div>
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-900">Community Gatherings</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <ChevronRight className="w-5 h-5 mr-2 text-green-600 mt-0.5" />
                    <span>Regular community activities</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="w-5 h-5 mr-2 text-green-600 mt-0.5" />
                    <span>Peer-to-peer learning circles</span>
                  </li>
                  <li className="flex items-start">
                    <ChevronRight className="w-5 h-5 mr-2 text-green-600 mt-0.5" />
                    <span>Knowledge sharing sessions</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Passport Society Modal */}
      <Dialog open={isPassportModalOpen} onOpenChange={setIsPassportModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              The International Passport Society
            </DialogTitle>
          </DialogHeader>
          <div className="mt-6 space-y-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-900">Global Experiences</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <ChevronRight className="w-5 h-5 mr-2 text-green-600 mt-0.5" />
                  <span>Curated travel experiences worldwide</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-5 h-5 mr-2 text-green-600 mt-0.5" />
                  <span>Cultural immersion programs</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-5 h-5 mr-2 text-green-600 mt-0.5" />
                  <span>International networking opportunities</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-5 h-5 mr-2 text-green-600 mt-0.5" />
                  <span>Personal growth through global exploration</span>
                </li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-900">Benefits</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <ChevronRight className="w-5 h-5 mr-2 text-green-600 mt-0.5" />
                  <span>Enhanced cultural understanding</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-5 h-5 mr-2 text-green-600 mt-0.5" />
                  <span>Improved adaptability and resilience</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-5 h-5 mr-2 text-green-600 mt-0.5" />
                  <span>Global perspective and awareness</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-5 h-5 mr-2 text-green-600 mt-0.5" />
                  <span>Lifelong international connections</span>
                </li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Experiences;
